/* WeQuote Dev Board — the Schedule modal (v2 §3, verify §71). Pick a dev +
 * start date; the ESTIMATE is required so scheduling always yields a
 * projected completion and a slot, with a live preview. Staged cards list
 * their stages instead — tick which to schedule and give each a start
 * (assignee stays per-stage, the locked decision). */
import React, { useState } from 'react'
import { useDevBoard } from '../DevBoardProvider'
import * as SC from '../core/schedule'
import type { Feature } from '../core/types'
import { DEVCOLS, DL, TIME_OPTS, timeLabel } from '../core/constants'
import { todayISO, fmtDate, fmtDayName } from '../core/dates'
import * as CAL from '../core/calendar'

export const ScheduleModalBody: React.FC<{ feat: Feature; onClose: () => void }> = ({ feat, onClose }) => {
  const { mutate, blockouts, cards } = useDevBoard()
  const [dev, setDev] = useState(feat.dev || '')
  const [start, setStart] = useState(feat.start || todayISO())
  const [time, setTime] = useState(feat.time || '')
  const [picks, setPicks] = useState<Record<number, { on: boolean; start: string; time: string }>>(() => {
    const m: Record<number, { on: boolean; start: string; time: string }> = {}
    feat.stages.forEach((sg) => { m[sg.id] = { on: !sg.done, start: sg.start || todayISO(), time: '' } })
    return m
  })
  if (feat.multiStage) {
    const doStaged = () => {
      const arr: SC.StagePick[] = feat.stages.filter((sg) => picks[sg.id] && picks[sg.id].on && !sg.done)
        .map((sg) => ({ sid: sg.id, start: picks[sg.id].start, time: picks[sg.id].time || undefined }))
      mutate((fs) => SC.confirmScheduleStaged(fs, feat.id, arr))
      const f2 = feat.stages.some((sg) => picks[sg.id] && picks[sg.id].on && !sg.done && picks[sg.id].start)
      if (f2) onClose()
    }
    return (
      <>
        <h2>Schedule stages — {feat.title}</h2>
        <div className="modal-sub">Tick the stage(s) to schedule and give each a start date. Each stage keeps its own assignee.</div>
        {feat.stages.map((sg, i) => (
          <div key={sg.id} className="sch-stage">
            <label className="sch-ck">
              <input type="checkbox" disabled={sg.done} checked={!!(picks[sg.id] && picks[sg.id].on) && !sg.done}
                onChange={(e) => setPicks((m) => ({ ...m, [sg.id]: { ...m[sg.id], on: e.target.checked } }))} />
              {' '}Stage {i + 1}{sg.name ? ': ' + sg.name : ''}{sg.done ? ' (done)' : ''}
            </label>
            <div className="sch-fields">
              <span className="sch-who">{sg.dev && DL[sg.dev] ? DL[sg.dev] : (feat.dev && DL[feat.dev] ? DL[feat.dev] : 'Unassigned')}</span>
              <label className="stage-lbl">Start</label>
              <input type="date" value={picks[sg.id]?.start || ''} onChange={(e) => setPicks((m) => ({ ...m, [sg.id]: { ...m[sg.id], start: e.target.value } }))} />
              {sg.time
                ? <span className="sch-est">{timeLabel(sg.time)}</span>
                : <><label className="stage-lbl">Estimate (required)</label>
                    <select value={picks[sg.id]?.time || ''} onChange={(e) => setPicks((m) => ({ ...m, [sg.id]: { ...m[sg.id], time: e.target.value } }))}>
                      {TIME_OPTS.map((o) => <option key={o.val || 'tbd'} value={o.val}>{o.label}</option>)}
                    </select></>}
            </div>
          </div>
        ))}
        <div className="factions"><button className="btn-s" onClick={onClose}>Cancel</button><button className="btn-p" onClick={doStaged}>Schedule stages</button></div>
      </>
    )
  }
  const preview = time ? SC.previewCompletion(start, time, dev, blockouts) : ''
  const doSchedule = () => {
    mutate((fs) => SC.confirmSchedule(fs, feat.id, dev, start, time))
    if (time && start) onClose()
  }
  const nextAvail = CAL.devNextAvailable(dev || (feat.dev || ''), cards, blockouts)
  /* week grid state: Monday of the shown week */
  const monday = (iso: string) => {
    const d = new Date(iso + 'T12:00:00')
    const w = d.getDay()
    d.setDate(d.getDate() + (w === 0 ? -6 : 1 - w))
    return d.toISOString().slice(0, 10)
  }
  const thisMonday = monday(todayISO())
  const [weekA, setWeekA] = useState(monday(start || todayISO()))
  const days = Array.from({ length: 7 }, (_, i) => CAL.calDaysISO(weekA, i))
  const weekLbl = weekA === thisMonday ? 'This week' : fmtDate(days[0]) + ' – ' + fmtDate(days[6])
  const today = todayISO()
  return (
    <>
      <h2>Schedule — {feat.title}</h2>
      <div className="sch2">
        <div className="sch2-left">
          <div className="fg"><label>Assign to</label>
            <select value={dev} onChange={(e) => setDev(e.target.value)}>
              <option value="">Unassigned</option>
              {DEVCOLS.map((d) => <option key={d} value={d}>{DL[d]}</option>)}
            </select>
          </div>
          <div className="fg"><label>Est dev time (required)</label>
            <select value={time} onChange={(e) => setTime(e.target.value)}>
              {TIME_OPTS.map((o) => <option key={o.val || 'tbd'} value={o.val}>{o.label}</option>)}
            </select>
          </div>
          <div className="fg"><label>Start date</label><input type="date" value={start} onChange={(e) => { setStart(e.target.value); if (e.target.value) setWeekA(monday(e.target.value)) }} /></div>
          <button className={'sch-next' + (start === nextAvail ? ' on' : '')} onClick={() => { setStart(nextAvail); setWeekA(monday(nextAvail)) }}>
            <span className="sch-next-k">Next availability:</span> {fmtDayName(nextAvail)}
          </button>
          <div className="sch-preview">{time
            ? (preview ? <>Projected completion: <b>{fmtDate(preview)}</b></> : null)
            : <span className="sch-warn">Pick an estimate to see the completion date and slot.</span>}</div>
        </div>
        <div className="sch2-right">
          <div className="schcal-hd">
            <button className="cal-navbtn" onClick={() => setWeekA(CAL.calDaysISO(weekA, -7))}>‹</button>
            <span className="schcal-lbl">{weekLbl}</span>
            <button className="cal-navbtn" onClick={() => setWeekA(CAL.calDaysISO(weekA, 7))}>›</button>
          </div>
          <div className="schcal-grid">
            {days.map((iso, i) => {
              const d = new Date(iso + 'T12:00:00')
              const we = i >= 5
              const cls = 'schcal-day' + (we ? ' we' : '') + (iso === start ? ' sel' : '') + (iso === today ? ' today' : '') + (iso < today ? ' past' : '') + (iso === nextAvail ? ' avail' : '')
              return (
                <div key={iso} className={cls} title={fmtDayName(iso) + (iso === nextAvail ? ' — next availability' : '')}
                  onClick={() => { setStart(iso) }}>
                  <span className="schcal-dw">{['M', 'T', 'W', 'T', 'F', 'S', 'S'][i]}</span>
                  <span className="schcal-dn">{d.getDate()}</span>
                  {iso === nextAvail && <span className="schcal-dot" />}
                </div>
              )
            })}
          </div>
          <div className="schcal-note">Click a day to set the start date{start ? ' · starting ' + fmtDayName(start) : ''}</div>
        </div>
      </div>
      <div className="factions"><button className="btn-s" onClick={onClose}>Cancel</button><button className="btn-p" onClick={doSchedule}>Schedule</button></div>
    </>
  )
}
